/**
 * POPSTAR network shell — the navigation hub for the "collect" side of the app.
 * Human-timescale only: it owns which network section is showing and renders the
 * real built screens EMBEDDED inside ONE persistent chrome, so every sub-screen
 * lives under the SAME header/tab system instead of carrying its own (owner spec).
 *
 * UNIFIED CHROME (the redesign): a single `.ps-chrome` at the top —
 *   • `.ps-topbar`: a "‹ Game" exit, the POPSTAR wordmark (→ Home), and the
 *     profile-stats card on the right (avatar, name, RANK badge, XP bar, tokens)
 *     with a gear menu for Settings/Admin (NOT a tab); and
 *   • `.ps-tabs`: FIVE tabs — Home · Profile · Stickers · Stats · LIVE.
 * The chrome is rendered ONCE, above the active section, so the tab bar never
 * "disappears" when you move between sections, and each embedded screen suppresses
 * its own back-header (the `embedded` prop) so there's a single header system.
 *
 * SECTIONS:
 *   • Home     — the in-app News feed (the landing; no redundant section buttons).
 *   • Profile  — ONE landscape VIP member card (no sub-nav): equipped sticker,
 *                featured starred replay, rank/level/XP, star points. Its
 *                "Change replay" action NAVIGATES to Stats → My Replays (the card
 *                showcases; the manager lives with the stats).
 *   • Stickers — sub-nav Management / Prize Wheel / Seasonal (the wheel moved in
 *                here; Seasonal lights up when an event is claimable).
 *   • Stats    — a GAME PICKER (POPSTAR is multi-game) + sub-nav Network / Personal
 *                / My Replays / Achievements. Network is the community aggregate;
 *                Personal is the per-game survival record (deaths donut counts EVERY
 *                run); My Replays is the replay library (watch/star — moved here
 *                from Profile); Achievements shows unlocks + per-player counts.
 *   • LIVE     — POPSTAR LIVE (its own tab, per the owner).
 *
 * TIER GATE: an `anon` user sees the "log in to collect" tease (named-only tabs are
 * hidden); a `named` user gets the full chrome. REPLAYS/showcase flow is unchanged:
 * a loaded {@link RunRecord} bubbles up via {@link NetworkShellProps.onWatchReplay}.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { RunRecord } from '../core/types';
import type { AccountTier, GameServices } from '../services/types';
import type {
  GameId,
  NetworkStats,
  StickerCatalogItem,
  UserProfile,
} from '../services/dataModel';
import { DEFAULT_GAME_ID, GAMES, emptyNetworkStats } from '../services/dataModel';
import { levelForXp } from '../core/xp';
import { AuthScreen } from './AuthScreen';
import { ProfileScreen } from './ProfileScreen';
import { ReplaysScreen } from './ReplaysScreen';
import { SettingsScreen } from './SettingsScreen';
import { AdminPanel } from './AdminPanel';
import { StickerShop } from './StickerShop';
import { WheelScreen } from './WheelScreen';
import { StatsScreen } from './StatsScreen';
import type { DeathsByFamily, PlayerStats } from './StatsScreen';
import { HomeDashboard } from './HomeDashboard';
import { LiveScreen } from './LiveScreen';
import { NetworkStatsView } from './NetworkStatsView';
import { AchievementsView } from './AchievementsView';
import { SeasonalView } from './SeasonalView';
import { RankBadge } from './RankBadge';
import { useT } from './i18n';

/** The five MAIN tabs of the POPSTAR hub. */
type MainTab = 'home' | 'profile' | 'stickers' | 'stats' | 'live';
/** Stickers sub-route ('rewards' = the merged Prize Wheel + seasonal events). */
type StickerPane = 'manage' | 'rewards';
/** Stats sub-route ('replays' = the replay library, moved here from Profile). */
type StatsPane = 'network' | 'personal' | 'replays' | 'achievements';
/** Full-screen overlays reached from the gear (NOT tabs). */
type Overlay = 'auth' | 'settings' | 'admin' | null;

/** A deep-link consumed once on mount (e.g. the game-over level-up → Prize Wheel). */
export interface ShellRoute {
  tab: MainTab;
  stickerPane?: StickerPane;
  statsPane?: StatsPane;
}

interface NetworkShellProps {
  services: GameServices;
  /** Current auth tier, owned by the parent so it stays the source of truth. */
  tier: AccountTier;
  /** Live "players online" count (for the Stats → Network view). */
  onlineCount: number;
  /** Leave the network section entirely (back to the main menu / game). */
  onExit: () => void;
  /** Tell the parent the auth tier may have changed (re-read `services.tier`). */
  onTierChange: () => void;
  /** Launch the deterministic replay PLAYER with a fully-loaded {@link RunRecord}. */
  onWatchReplay: (record: RunRecord) => void;
  /** Start today's DAILY RUN (exits the network section and boots the game). */
  onPlayDaily?: () => void;
  /** Optional deep-link applied once on mount (e.g. open Stickers → Prize Wheel). */
  initialRoute?: ShellRoute | null;
  /**
   * Fired once after {@link initialRoute} has been applied on mount, so the parent
   * can clear it — otherwise a later remount (e.g. returning from a replay) would
   * re-apply the stale deep-link instead of landing on the user's place.
   */
  onRouteConsumed?: () => void;
}

/** The five tabs, in display order, with their labels + icons. */
const MAIN_TABS: ReadonlyArray<{ id: MainTab; label: string; icon: string }> = [
  { id: 'home', label: 'Home', icon: '📰' },
  { id: 'profile', label: 'Profile', icon: '👤' },
  { id: 'stickers', label: 'Stickers', icon: '🎟️' },
  { id: 'stats', label: 'Stats', icon: '📊' },
  { id: 'live', label: 'LIVE', icon: '🔴' },
];

const EMPTY_DEATHS: DeathsByFamily = {
  walls: 0,
  lasers: 0,
  turret: 0,
  caro: 0,
  missiles: 0,
  bombs: 0,
};

/**
 * Derive the presentational {@link PlayerStats} for the Personal tab from the
 * server-maintained PER-GAME aggregate on the profile (`gameStats[gameId]`). This
 * counts EVERY validated run (not just saved replays), which is what makes the
 * deaths donut populate — the previous shell derived it from the saved-only
 * `getMyReplays`, so deaths were almost always empty.
 */
function playerStatsFromProfile(profile: UserProfile | null, gameId: GameId): PlayerStats {
  const gs = profile?.gameStats?.[gameId];
  if (!gs) {
    return {
      runs: 0,
      bestScore: 0,
      averageScore: 0,
      longestSurvivalMs: 0,
      totalDodged: 0,
      deaths: { ...EMPTY_DEATHS },
      deathHeat: [],
    };
  }
  return {
    runs: gs.runs,
    bestScore: gs.bestScore,
    averageScore: gs.runs ? Math.round(gs.scoreSum / gs.runs) : 0,
    longestSurvivalMs: gs.longestMs,
    totalDodged: gs.totalAvoided,
    deaths: { ...EMPTY_DEATHS, ...(gs.deathsByFamily ?? {}) },
    // Older docs predate the heat feature — read back as empty (fills in as they play).
    deathHeat: gs.deathHeat ?? [],
  };
}

export function NetworkShell({
  services,
  tier,
  onlineCount,
  onExit,
  onTierChange,
  onWatchReplay,
  onPlayDaily,
  initialRoute = null,
  onRouteConsumed,
}: NetworkShellProps) {
  const t = useT();
  const [tab, setTab] = useState<MainTab>(initialRoute?.tab ?? 'home');
  const [stickerPane, setStickerPane] = useState<StickerPane>(initialRoute?.stickerPane ?? 'manage');
  const [statsPane, setStatsPane] = useState<StatsPane>(initialRoute?.statsPane ?? 'network');
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [gameId, setGameId] = useState<GameId>(DEFAULT_GAME_ID);
  // Bumped after any in-tab economy change (wheel spin / sticker buy) so the chrome
  // profile card re-reads the fresh XP/token balance without a tab switch.
  const [econ, setEcon] = useState(0);
  const bumpEcon = useCallback(() => setEcon((n) => n + 1), []);

  // The deep-link is consumed by the useState initializers above (first mount only);
  // tell the parent to clear it so a later remount doesn't re-apply the stale route.
  useEffect(() => {
    if (initialRoute) onRouteConsumed?.();
    // Run exactly once on mount — initialRoute/onRouteConsumed are captured then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goTab = useCallback((next: MainTab) => {
    setTab(next);
  }, []);

  // The VIP card's "set/change your featured replay" hand-off: the replay
  // library lives under Stats, so navigate there instead of managing in place.
  const goReplayLibrary = useCallback(() => {
    setTab('stats');
    setStatsPane('replays');
  }, []);

  // The VIP card's empty sticker frame → Stickers management to equip one.
  const goStickers = useCallback(() => {
    setTab('stickers');
    setStickerPane('manage');
  }, []);

  const handleAuthed = useCallback(() => {
    onTierChange();
    setOverlay(null);
    setTab('home');
  }, [onTierChange]);

  const handleLoggedOut = useCallback(() => {
    onTierChange();
    setOverlay(null);
    setTab('home');
  }, [onTierChange]);

  // ProfileScreen hands up a runId; resolve it to a full record before bubbling.
  const watchById = useCallback(
    (runId: string) => {
      void (async () => {
        const record = await services.getReplay(runId);
        if (record) onWatchReplay(record);
      })();
    },
    [services, onWatchReplay],
  );

  const closeOverlay = useCallback(() => setOverlay(null), []);

  // --- header-reachable overlays (full-screen; their own back header) ---------
  if (overlay === 'auth') {
    return <AuthScreen services={services} onAuthed={handleAuthed} onBack={closeOverlay} />;
  }
  if (overlay === 'settings') {
    return (
      <SettingsScreen services={services} onBack={closeOverlay} onLoggedOut={handleLoggedOut} />
    );
  }
  if (overlay === 'admin') {
    return <AdminPanel services={services} onBack={closeOverlay} />;
  }

  // --- anon tease (no tabs; just the upsell + a way back to the game) ---------
  if (tier === 'anon') {
    return (
      <div className="screen network-screen network-hub">
        <style>{SHELL_CSS}</style>
        <div className="ps-topbar ps-topbar-anon">
          <button className="ps-exit" type="button" onClick={onExit}>
            {t('popstar.exitGame')}
          </button>
          <span className="ps-brand" aria-hidden>
            POPSTAR
          </span>
          <button className="btn btn-primary ps-login" type="button" onClick={() => setOverlay('auth')}>
            {t('menu.login')}
          </button>
        </div>
        <AnonTease onLogIn={() => setOverlay('auth')} />
      </div>
    );
  }

  // --- the tabbed hub (named) -------------------------------------------------
  return (
    <div className="screen network-screen network-hub">
      <style>{SHELL_CSS}</style>

      <Chrome
        services={services}
        tab={tab}
        refreshKey={`${tab}:${econ}`}
        onTab={goTab}
        onExit={onExit}
        onSettings={() => setOverlay('settings')}
        onAdmin={() => setOverlay('admin')}
      />

      <div className="ps-body">
        {tab === 'home' && (
          <HomeDashboard
            services={services}
            onlineCount={onlineCount}
            onGoStats={() => {
              setTab('stats');
              setStatsPane('network');
            }}
            onGoRewards={() => {
              setTab('stickers');
              setStickerPane('rewards');
            }}
            onPlayDaily={onPlayDaily}
          />
        )}

        {tab === 'profile' && (
          <div className="ps-pane">
            <ProfileScreen
              services={services}
              onBack={() => {}}
              onWatch={watchById}
              onChangeReplay={goReplayLibrary}
              onGoStickers={goStickers}
              embedded
            />
          </div>
        )}

        {tab === 'stickers' && (
          <StickersTab
            services={services}
            pane={stickerPane}
            onPane={setStickerPane}
            onEconomyChange={bumpEcon}
          />
        )}

        {tab === 'stats' && (
          <StatsTab
            services={services}
            gameId={gameId}
            onGameId={setGameId}
            pane={statsPane}
            onPane={setStatsPane}
            onlineCount={onlineCount}
            onWatchReplay={onWatchReplay}
          />
        )}

        {tab === 'live' && (
          <LiveScreen
            services={services}
            onWatchReplay={onWatchReplay}
            onBack={() => goTab('home')}
            embedded
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chrome — the single persistent top bar (exit + brand + profile card) + tabs.
// ---------------------------------------------------------------------------

function Chrome({
  services,
  tab,
  refreshKey,
  onTab,
  onExit,
  onSettings,
  onAdmin,
}: {
  services: GameServices;
  tab: MainTab;
  /** Changes on tab navigation AND after an in-tab economy change, to re-read the card. */
  refreshKey: string;
  onTab: (t: MainTab) => void;
  onExit: () => void;
  onSettings: () => void;
  onAdmin: () => void;
}) {
  const t = useT();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [catalog, setCatalog] = useState<StickerCatalogItem[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);

  // Re-read whenever the refresh key changes: on tab navigation AND after an in-tab
  // economy change (wheel spin / sticker buy / equip) so the XP bar, token wallet,
  // AND the equipped-sticker avatar stay fresh.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [p, cat] = await Promise.all([
          services.getProfile(),
          services.getStickerCatalog().catch(() => [] as StickerCatalogItem[]),
        ]);
        if (!cancelled) {
          setProfile(p);
          setCatalog(cat);
        }
      } catch {
        /* anon / fetch failure — leave the card quiet */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services, refreshKey]);

  const lvl = profile ? levelForXp(profile.xp) : null;
  const span = lvl ? lvl.intoLevel + lvl.toNext : 0;
  const xpPct = lvl && span > 0 ? Math.round((lvl.intoLevel / span) * 100) : 0;
  // The "profile photo" IS the equipped sticker (owner spec) — slot 1 fills the
  // avatar, slot 2 rides as a mini badge; a bare ★ invites equipping when empty.
  const slot1 = profile?.equipped.slot1
    ? catalog.find((s) => s.id === profile.equipped.slot1) ?? null
    : null;
  const slot2 = profile?.equipped.slot2
    ? catalog.find((s) => s.id === profile.equipped.slot2) ?? null
    : null;

  return (
    <div className="ps-chrome">
      <div className="ps-topbar">
        <button className="ps-exit" type="button" onClick={onExit}>
          {t('popstar.exitGame')}
        </button>
        <button className="ps-brand" type="button" onClick={() => onTab('home')}>
          POPSTAR
        </button>

        <div className="ps-card-wrap">
          <button className="ps-card" type="button" onClick={() => onTab('profile')} aria-label="Open your profile">
            <span className={'ps-card-avatar' + (slot1 ? ' has-sticker' : '')} aria-hidden>
              {slot1 ? (
                slot1.imageUrl ? (
                  <img className="ps-card-avatar-art" src={slot1.imageUrl} alt="" draggable={false} />
                ) : (
                  <span className="ps-card-avatar-ph">◆</span>
                )
              ) : (
                '★'
              )}
              {slot2 && (
                <span className="ps-card-avatar-mini">
                  {slot2.imageUrl ? (
                    <img src={slot2.imageUrl} alt="" draggable={false} />
                  ) : (
                    '◆'
                  )}
                </span>
              )}
            </span>
            <span className="ps-card-body">
              <span className="ps-card-name">{profile?.name ?? '…'}</span>
              {lvl && <RankBadge level={lvl.level} variant="chip" className="ps-card-rank" />}
              <span className="ps-card-xp" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={xpPct}>
                <i style={{ width: `${xpPct}%` }} />
              </span>
            </span>
            <span className="ps-card-tokens" title="Token wallet">
              <span aria-hidden>◈</span>
              {profile ? profile.tokens.toLocaleString() : '—'}
            </span>
          </button>

          <div className="ps-gear-wrap">
            <button
              className="btn btn-ghost ps-gear"
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              aria-label="Settings menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              ⚙️
            </button>
            {menuOpen && (
              <div className="ps-gear-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  className="ps-gear-item"
                  onClick={() => {
                    setMenuOpen(false);
                    onSettings();
                  }}
                >
                  ⚙️ Settings
                </button>
                {services.isAdmin() && (
                  <button
                    type="button"
                    role="menuitem"
                    className="ps-gear-item"
                    onClick={() => {
                      setMenuOpen(false);
                      onAdmin();
                    }}
                  >
                    🛠️ Admin
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <nav className="ps-tabs" aria-label="POPSTAR sections">
        {MAIN_TABS.map((tb) => (
          <button
            key={tb.id}
            type="button"
            className={tb.id === tab ? 'ps-tab active' : 'ps-tab'}
            aria-current={tb.id === tab ? 'page' : undefined}
            onClick={() => onTab(tb.id)}
          >
            <span className="ps-tab-icon" aria-hidden>
              {tb.icon}
            </span>
            {t(`nav.${tb.id}`, tb.label)}
          </button>
        ))}
      </nav>
    </div>
  );
}

/** A pill button for an in-tab sub-nav. */
function SubNavButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={active ? 'ps-subnav-btn active' : 'ps-subnav-btn'}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Stickers tab — Management / Prize Wheel / Seasonal.
// ---------------------------------------------------------------------------

function StickersTab({
  services,
  pane,
  onPane,
  onEconomyChange,
}: {
  services: GameServices;
  pane: StickerPane;
  onPane: (p: StickerPane) => void;
  /** Called after a wheel spin / sticker buy so the chrome card refreshes. */
  onEconomyChange: () => void;
}) {
  const t = useT();
  return (
    <div className="ps-pane">
      <div className="ps-subnav" role="tablist" aria-label="Stickers views">
        <SubNavButton active={pane === 'manage'} onClick={() => onPane('manage')}>
          {t('stickers.collection')}
        </SubNavButton>
        <SubNavButton active={pane === 'rewards'} onClick={() => onPane('rewards')}>
          {t('stickers.rewards')}
        </SubNavButton>
      </div>

      {pane === 'manage' && (
        <StickerShop services={services} onBack={() => {}} onEconomyChange={onEconomyChange} embedded />
      )}
      {pane === 'rewards' && (
        <>
          {/* Rewards = every way to WIN: the claimable event (when live) rides
              above the always-available Prize Wheel. */}
          <SeasonalView services={services} />
          <WheelView services={services} onEconomyChange={onEconomyChange} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stats tab — game picker + Network / Personal / Achievements.
// ---------------------------------------------------------------------------

function StatsTab({
  services,
  gameId,
  onGameId,
  pane,
  onPane,
  onlineCount,
  onWatchReplay,
}: {
  services: GameServices;
  gameId: GameId;
  onGameId: (g: GameId) => void;
  pane: StatsPane;
  onPane: (p: StatsPane) => void;
  onlineCount: number;
  /** Launch the replay player (the My Replays pane hands up a loaded record). */
  onWatchReplay: (record: RunRecord) => void;
}) {
  const t = useT();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [net, setNet] = useState<NetworkStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      const [p, n] = await Promise.all([
        services.getProfile().catch(() => null),
        services.getNetworkStats(gameId).catch(() => null),
      ]);
      if (!cancelled) {
        setProfile(p);
        setNet(n);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services, gameId]);

  const personal = useMemo(() => playerStatsFromProfile(profile, gameId), [profile, gameId]);
  const gs = profile?.gameStats?.[gameId];
  const unlocked = gs?.unlockedAchievements ?? [];
  // Merge over the zeroed defaults so a server that predates newer aggregate
  // fields (hourly average, deaths, heat) can't hand the charts undefined.
  const netStats = { ...emptyNetworkStats(gameId), ...(net ?? {}) };
  const games = Object.values(GAMES);

  return (
    <div className="ps-pane">
      <div className="ps-stats-toolbar">
        <label className="ps-game-picker">
          <span className="ps-game-picker-label">{t('stats.game')}</span>
          <select
            value={gameId}
            onChange={(e) => onGameId(e.target.value as GameId)}
            aria-label="Choose a game"
          >
            {games.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </label>

        <div className="ps-subnav" role="tablist" aria-label="Stats views">
          <SubNavButton active={pane === 'network'} onClick={() => onPane('network')}>
            {t('stats.network')}
          </SubNavButton>
          <SubNavButton active={pane === 'personal'} onClick={() => onPane('personal')}>
            {t('stats.personal')}
          </SubNavButton>
          <SubNavButton active={pane === 'replays'} onClick={() => onPane('replays')}>
            {t('profile.myReplays')}
          </SubNavButton>
          <SubNavButton active={pane === 'achievements'} onClick={() => onPane('achievements')}>
            {t('stats.achievements')}
          </SubNavButton>
        </div>
      </div>

      {pane === 'network' && (
        <NetworkStatsView
          stats={netStats}
          onlineCount={onlineCount}
          gameName={GAMES[gameId].name}
          loading={loading}
        />
      )}
      {pane === 'personal' && <StatsScreen stats={personal} onBack={() => {}} embedded />}
      {pane === 'replays' && (
        <ReplaysScreen
          services={services}
          gameId={gameId}
          onWatch={onWatchReplay}
          onBack={() => onPane('personal')}
          embedded
        />
      )}
      {pane === 'achievements' && (
        <AchievementsView
          unlocked={unlocked}
          counts={netStats.achievementCounts}
          playersTracked={netStats.playersTracked}
          loading={loading}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Thin one-shot data loaders (News / Wheel) for the presentational screens.
// ---------------------------------------------------------------------------

function WheelView({
  services,
  onEconomyChange,
}: {
  services: GameServices;
  onEconomyChange: () => void;
}) {
  const [tokens, setTokens] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await services.getProfile();
        if (!cancelled) setTokens(profile.tokens);
      } catch {
        /* anon / fetch failure: WheelScreen still renders (spin is gated server-side) */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [services]);

  return (
    <WheelScreen
      services={services}
      tokens={tokens}
      onBack={() => {}}
      onEconomyChange={onEconomyChange}
      embedded
    />
  );
}

// ---------------------------------------------------------------------------
// Anon tease — the "log in to collect" experience for anonymous users.
// ---------------------------------------------------------------------------

const LOCKED_FEATURES: ReadonlyArray<{ icon: string; title: string; blurb: string }> = [
  { icon: '⭐', title: 'XP, levels & ranks', blurb: 'Climb 256 ranks from Soundcheck to POPSTAR.' },
  { icon: '🎟️', title: 'Stickers', blurb: 'Collect and equip stickers on your ball.' },
  { icon: '🪙', title: 'Tokens', blurb: 'Bank tokens to spend on cosmetics.' },
  { icon: '🎡', title: 'Prize wheel', blurb: 'Spin for stickers and bonus tokens.' },
  { icon: '🏅', title: 'Achievements', blurb: 'Unlock milestones and track your stats.' },
];

function AnonTease({ onLogIn }: { onLogIn: () => void }) {
  const t = useT();
  return (
    <>
      <section className="panel glass tease-hero">
        <h2 className="tease-headline">{t('tease.headline')}</h2>
        <p className="tease-sub">{t('tease.sub')}</p>
        <button className="btn btn-primary tease-cta" type="button" onClick={onLogIn}>
          {t('tease.cta')}
        </button>
      </section>

      <section className="panel glass">
        <h2>{t('tease.unlock')}</h2>
        <ul className="tease-list">
          {LOCKED_FEATURES.map((f) => (
            <li className="tease-item" key={f.title}>
              <span className="tease-icon" aria-hidden>
                {f.icon}
              </span>
              <span className="tease-text">
                <span className="tease-item-title">{f.title}</span>
                <span className="tease-item-blurb">{f.blurb}</span>
              </span>
              <span className="tease-lock" aria-label="locked">
                🔒
              </span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

/**
 * Shell-scoped chrome styles (the `ps-*` vocabulary). Self-contained — every value
 * reads from the glass design tokens in src/index.css, nested under `.network-hub`
 * so it can't leak. This replaces the old `.popstar-*` chrome (the stacked
 * title/card/tabs the owner flagged) with one unified top bar + tab row.
 */
const SHELL_CSS = `
.network-hub.network-screen { gap: 0; }

/* ---- The single chrome: top bar + tabs (persistent across sections) ----
   In normal flow (NOT sticky): it sits at the top of the scrollable hub and is
   rendered for every section, so it never "disappears" when switching tabs. A
   sticky variant caused background-masking + side-gap artefacts, so we keep it
   simple and predictable. */
.network-hub .ps-chrome {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 0 14px;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--glass-border);
}
.network-hub .ps-topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
}
.network-hub .ps-topbar-anon { margin-bottom: 14px; }
.network-hub .ps-exit {
  flex: 0 0 auto;
  font: inherit;
  font-weight: 700;
  font-size: 14px;
  color: var(--text-dim);
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 8px 14px;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease), border-color var(--t-fast) var(--ease);
}
.network-hub .ps-exit:hover { color: var(--text); border-color: var(--glass-border-strong); }
.network-hub .ps-brand {
  font-weight: 900;
  font-size: 20px;
  letter-spacing: 0.12em;
  background: linear-gradient(96deg, var(--accent), var(--gold));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  background-color: transparent;
  border: 0;
  cursor: pointer;
  padding: 0 4px;
}
.network-hub .ps-login { margin-left: auto; padding: 8px 16px; }

/* Profile-stats card (right of the top bar). */
.network-hub .ps-card-wrap {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.network-hub .ps-card {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  max-width: 320px;
  padding: 7px 12px;
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-1), inset 0 1px 0 var(--glass-hi);
  cursor: pointer;
  transition: border-color var(--t-fast) var(--ease), transform var(--t-fast) var(--ease);
}
.network-hub .ps-card:hover { border-color: var(--glass-border-strong); transform: translateY(-1px); }
.network-hub .ps-card-avatar {
  position: relative;
  flex: 0 0 auto;
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  font-weight: 900;
  color: var(--accent-ink);
  background: linear-gradient(135deg, var(--accent), var(--gold));
}
.network-hub .ps-card-avatar.has-sticker {
  background: var(--panel-2);
  box-shadow: inset 0 0 0 2px var(--gold);
}
.network-hub .ps-card-avatar-art {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.network-hub .ps-card-avatar-ph { font-size: 15px; color: var(--accent-soft); }
.network-hub .ps-card-avatar-mini {
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  overflow: hidden;
  font-size: 9px;
  color: var(--accent-soft);
  background: var(--panel-2);
  border: 2px solid var(--panel-strong);
}
.network-hub .ps-card-avatar-mini img { width: 100%; height: 100%; object-fit: cover; }
.network-hub .ps-card-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.network-hub .ps-card-name {
  font-weight: 800;
  font-size: 14px;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.network-hub .ps-card-xp {
  width: 120px;
  max-width: 28vw;
  height: 5px;
  border-radius: var(--radius-pill);
  background: var(--panel-2);
  overflow: hidden;
}
.network-hub .ps-card-xp i {
  display: block;
  height: 100%;
  border-radius: var(--radius-pill);
  background: linear-gradient(90deg, var(--accent), var(--gold));
}
.network-hub .ps-card-tokens {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 800;
  font-size: 13px;
  color: var(--gold);
  font-variant-numeric: tabular-nums;
}

/* Gear menu. */
.network-hub .ps-gear-wrap { position: relative; flex: 0 0 auto; }
.network-hub .ps-gear {
  width: 40px;
  height: 40px;
  padding: 0;
  font-size: 17px;
  line-height: 1;
  border-radius: var(--radius-sm);
}
.network-hub .ps-gear-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  min-width: 168px;
  padding: 6px;
  background: var(--panel-strong);
  border: 1px solid var(--glass-border-strong);
  border-radius: var(--radius);
  box-shadow: var(--shadow-2), inset 0 1px 0 var(--glass-hi);
  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));
}
.network-hub .ps-gear-item {
  display: flex;
  align-items: center;
  gap: 9px;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  color: var(--text);
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  cursor: pointer;
}
.network-hub .ps-gear-item:hover { background: var(--accent-tint); border-color: var(--glass-border); }

/* Tab bar — the single nav. FLAT UNDERLINE tabs (owner: no pill chrome):
   transparent row over a hairline, the active tab marked by a gradient bar. */
.network-hub .ps-tabs {
  display: flex;
  align-items: stretch;
  gap: 2px;
  width: 100%;
  max-width: 760px;
  margin: 0 auto;
  border-bottom: 1px solid var(--glass-border);
  overflow-x: auto;
  scrollbar-width: none;
}
.network-hub .ps-tabs::-webkit-scrollbar { display: none; }
.network-hub .ps-tab {
  position: relative;
  flex: 1 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--text-dim);
  background: transparent;
  border: 0;
  padding: 11px 14px 13px;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease);
}
.network-hub .ps-tab::after {
  content: '';
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: -1px;
  height: 3px;
  border-radius: 2px 2px 0 0;
  background: linear-gradient(90deg, var(--accent), var(--gold));
  opacity: 0;
  transform: scaleX(0.5);
  transition: opacity var(--t-fast) var(--ease), transform var(--t-fast) var(--ease);
}
.network-hub .ps-tab:hover:not(.active) { color: var(--text); }
.network-hub .ps-tab.active { color: var(--text); }
.network-hub .ps-tab.active::after { opacity: 1; transform: scaleX(1); }
.network-hub .ps-tab-icon { font-size: 14px; }

/* ---- Section body + sub-navs ---- */
.network-hub .ps-body {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.network-hub .ps-pane {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
/* Sub-navs share the flat underline language (no pills). */
.network-hub .ps-subnav {
  display: inline-flex;
  align-items: stretch;
  gap: 2px;
  border-bottom: 1px solid var(--glass-border);
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.network-hub .ps-subnav::-webkit-scrollbar { display: none; }
.network-hub .ps-subnav-btn {
  position: relative;
  flex: 0 0 auto;
  font: inherit;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  color: var(--text-dim);
  background: transparent;
  border: 0;
  padding: 8px 13px 11px;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease);
}
.network-hub .ps-subnav-btn::after {
  content: '';
  position: absolute;
  left: 13px;
  right: 13px;
  bottom: -1px;
  height: 2.5px;
  border-radius: 2px 2px 0 0;
  background: linear-gradient(90deg, var(--accent-soft), var(--accent));
  opacity: 0;
  transform: scaleX(0.5);
  transition: opacity var(--t-fast) var(--ease), transform var(--t-fast) var(--ease);
}
.network-hub .ps-subnav-btn:hover:not(.active) { color: var(--text); }
.network-hub .ps-subnav-btn.active { color: var(--text); }
.network-hub .ps-subnav-btn.active::after { opacity: 1; transform: scaleX(1); }

/* Stats toolbar: game picker + sub-nav. Constrained to the content width so it
   lines up with the panels below instead of floating free. */
.network-hub .ps-stats-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: min(560px, 94vw);
  margin: 0 auto;
}
.network-hub .ps-game-picker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--text-dim);
}
.network-hub .ps-game-picker-label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  font-size: 11px;
  color: var(--muted);
}
.network-hub .ps-game-picker select {
  font: inherit;
  font-weight: 700;
  font-size: 13px;
  color: var(--text);
  background: var(--panel);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-sm);
  padding: 7px 12px;
  cursor: pointer;
}

/* CRITICAL: the base .screen is a position:absolute, inset:0 FULL-VIEWPORT overlay.
   When a screen is embedded inside a tab it must instead flow IN-LINE under the
   chrome — otherwise every section stacks as a full-screen layer on top of the tab
   bar ("all over the place"). Neutralise the overlay positioning + its full-screen
   padding/background/animation so each embedded screen sits as a normal block. */
.network-hub .screen.is-embedded {
  position: static;
  inset: auto;
  z-index: auto;
  width: 100%;
  min-height: 0;
  overflow: visible;
  padding: 0;
  gap: 14px;
  background: none;
  animation: none;
}
`;
